"use client";

import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { 
  ChevronLeft, 
  ChevronRight, 
  LayoutGrid, 
  LayoutList,
  SlidersHorizontal,
  RefreshCw
} from 'lucide-react';
import PropertyCard, { PropertyCardProps } from './PropertyCard';
import { Button } from '@/components/ui/button';

interface PropertyGridProps {
  title: string;
  properties: PropertyCardProps['property'][];
  type: 'rent' | 'sale';
  isLoading?: boolean;
  className?: string;
}

const PropertyGrid = ({ 
  title, 
  properties, 
  type,
  isLoading = false,
  className = '' 
}: PropertyGridProps) => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const itemsPerPage = viewMode === 'grid' ? 9 : 5;
  
  // Reset to first page when properties change
  useEffect(() => {
    setCurrentPage(1);
  }, [properties]);
  
  // Calculate pagination values
  const totalPages = Math.ceil(properties.length / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const endIdx = startIdx + itemsPerPage;
  const currentProperties = properties.slice(startIdx, endIdx);
  
  // Handle page changes
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll to top of grid
      if (typeof window !== "undefined") {
        window.scrollTo({ top: window.scrollY - 200, behavior: "smooth" });
      }
    }
  };
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  
  // Get color classes based on type using design tokens
  const getColorClasses = (element: string) => {
    const baseClasses = {
      rent: {
        title: 'text-[var(--color-rent-primary)]',
        gradient: 'bg-gradient-to-r from-[var(--color-rent-primary)]/30 to-transparent',
        activeButton: 'bg-[var(--color-rent-primary)]/20 text-[var(--color-rent-primary)]',
        hoverButton: 'hover:bg-[var(--color-rent-primary)]/10',
        primaryButton: 'bg-[var(--color-rent-primary)] hover:bg-[var(--color-rent-primary)]/90',
        spinner: 'text-[var(--color-rent-primary)]',
        iconBg: 'bg-[var(--color-rent-primary)]/20',
        iconColor: 'text-[var(--color-rent-primary)]',
        borderColor: 'border-[var(--color-rent-primary)]/30'
      },
      sale: {
        title: 'text-[var(--color-sale-primary)]',
        gradient: 'bg-gradient-to-r from-[var(--color-sale-primary)]/30 to-transparent',
        activeButton: 'bg-[var(--color-sale-primary)]/20 text-[var(--color-sale-primary)]',
        hoverButton: 'hover:bg-[var(--color-sale-primary)]/10',
        primaryButton: 'bg-[var(--color-sale-primary)] hover:bg-[var(--color-sale-primary)]/90',
        spinner: 'text-[var(--color-sale-primary)]',
        iconBg: 'bg-[var(--color-sale-primary)]/20',
        iconColor: 'text-[var(--color-sale-primary)]',
        borderColor: 'border-[var(--color-sale-primary)]/30'
      }
    };
    
    return baseClasses[type][element as keyof typeof baseClasses[typeof type]] || '';
  };
  
  return (
    <section className={`${className}`}>
      <motion.div 
        className="flex items-center justify-between mb-8"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center gap-3">
          <h2 className={`text-2xl font-bold ${getColorClasses('title')}`}>{title}</h2>
          <div className={`h-px w-24 md:w-32 ${getColorClasses('gradient')}`}></div>
        </div>
        
        {/* Controls */}
        <div className="flex items-center gap-2">
          {/* View mode toggle */}
          <div className="hidden md:flex items-center border rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 transition-colors ${
                viewMode === 'grid'
                  ? getColorClasses('activeButton')
                  : 'bg-[var(--color-pure-white)] text-[var(--color-neutral-500)] hover:text-[var(--color-neutral-700)]'
              }`}
              aria-label="Grid view"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 transition-colors ${
                viewMode === 'list'
                  ? getColorClasses('activeButton')
                  : 'bg-[var(--color-pure-white)] text-[var(--color-neutral-500)] hover:text-[var(--color-neutral-700)]'
              }`}
              aria-label="List view"
            >
              <LayoutList className="h-4 w-4" />
            </button>
          </div>
          
          {/* Sort dropdown (placeholder) */}
          <Button
            variant="outline"
            size="sm"
            className="text-[var(--color-neutral-600)] border-[var(--color-neutral-300)]"
          >
            <SlidersHorizontal className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Sort</span>
          </Button>
        </div>
      </motion.div>
      
      {/* Loading state */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <RefreshCw className={`h-10 w-10 ${getColorClasses('spinner')} animate-spin mb-4`} />
          <p className="text-[var(--color-neutral-600)]">Loading properties...</p>
        </div>
      ) : properties.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-[var(--color-neutral-50)] rounded-xl">
          <div className={`p-4 rounded-full ${getColorClasses('iconBg')} mb-4`}>
            <svg className={`h-10 w-10 ${getColorClasses('iconColor')}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-[var(--color-neutral-800)] mb-2">No properties found</h3>
          <p className="text-[var(--color-neutral-600)] text-center max-w-md">
            We couldn't find any properties matching your search criteria. Try adjusting your filters.
          </p>
        </div>
      ) : (
        <>
          {/* Property Grid/List */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className={
              viewMode === 'grid'
                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
                : "flex flex-col gap-4"
            }
          >
            {currentProperties.map((property, index) => (
              <PropertyCard 
                key={property.id} 
                property={property}
                animationDelay={index * 0.05}
              />
            ))}
          </motion.div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-[var(--color-neutral-600)]">
                Showing <span className="font-medium">{startIdx + 1}</span> to{" "}
                <span className="font-medium">{Math.min(endIdx, properties.length)}</span> of{" "}
                <span className="font-medium">{properties.length}</span> properties
              </div>
              
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-0 h-8 w-8 rounded-md ${
                    currentPage === 1 
                      ? 'opacity-50 cursor-not-allowed' 
                      : `border-[var(--color-neutral-300)] ${getColorClasses('title')} ${getColorClasses('hoverButton')}`
                  }`}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                {/* Page buttons */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  // Handle ellipsis for lots of pages
                  let pageNum: number;
                  
                  if (totalPages <= 5) {
                    // Show all pages if 5 or fewer
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    // Near start
                    if (i < 4) {
                      pageNum = i + 1;
                    } else {
                      pageNum = totalPages;
                    }
                  } else if (currentPage >= totalPages - 2) {
                    // Near end
                    if (i === 0) {
                      pageNum = 1;
                    } else {
                      pageNum = totalPages - 4 + i;
                    }
                  } else {
                    // Middle
                    if (i === 0) {
                      pageNum = 1;
                    } else if (i === 4) {
                      pageNum = totalPages;
                    } else {
                      pageNum = currentPage - 1 + i;
                    }
                  }
                  
                  // Show ellipsis instead of a button
                  if ((i === 1 && pageNum !== 2) || (i === 3 && pageNum !== totalPages - 1)) {
                    return (
                      <span key={`ellipsis-${i}`} className="px-2 text-[var(--color-neutral-400)]">
                        …
                      </span>
                    );
                  }
                  
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      onClick={() => goToPage(pageNum)}
                      className={`p-0 h-8 w-8 rounded-md ${
                        currentPage === pageNum
                          ? `${getColorClasses('primaryButton')} text-[var(--color-pure-white)]`
                          : `border-[var(--color-neutral-300)] text-[var(--color-neutral-700)] ${getColorClasses('hoverButton')}`
                      }`}
                      aria-label={`Page ${pageNum}`}
                      aria-current={currentPage === pageNum ? "page" : undefined}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-0 h-8 w-8 rounded-md ${
                    currentPage === totalPages 
                      ? 'opacity-50 cursor-not-allowed' 
                      : `border-[var(--color-neutral-300)] ${getColorClasses('title')} ${getColorClasses('hoverButton')}`
                  }`}
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default PropertyGrid; 